import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  LayoutDashboard, 
  FileText, 
  Cat, 
  Star, 
  Settings, 
  LogOut,
  Mail,
  Users,
  Plus,
  ChevronRight,
  Image
} from 'lucide-react';
import AnalyticsWidget from '@/components/AnalyticsWidget';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

export default function AdminDashboard() {
  const { logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    blog_posts: 0,
    alumni: 0,
    testimonials: 0,
    contacts: 0,
    waitlist: 0
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/admin/stats`, {
        headers: { 'Authorization': getAuthHeader() }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const menuItems = [
    { icon: FileText, label: 'Articles du blog', count: stats.blog_posts, href: '/admin/blog', color: 'bg-blue-500' },
    { icon: Cat, label: 'Nos Chats', count: stats.cats, href: '/admin/cats', color: 'bg-forest' },
    { icon: Cat, label: 'Chatons adoptés', count: stats.alumni, href: '/admin/alumni', color: 'bg-amber-500' },
    { icon: Star, label: 'Témoignages', count: stats.testimonials, href: '/admin/testimonials', color: 'bg-yellow-500' },
    { icon: Mail, label: 'Messages reçus', count: stats.contacts, href: '/admin/contacts', color: 'bg-green-500' },
    { icon: Users, label: "Liste d'attente", count: stats.waitlist, href: '/admin/waitlist', color: 'bg-purple-500' },
    { icon: Image, label: 'Galerie Photos', count: null, href: '/admin/gallery', color: 'bg-teal-500' },
    { icon: Settings, label: 'Paramètres', count: null, href: '/admin/settings', color: 'bg-gray-500' },
  ];

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-forest text-white py-4 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🐱</span>
            <div>
              <h1 className="font-heading text-xl font-bold">La Taïga Siberia</h1>
              <p className="font-body text-xs text-sand/80">Panel d'administration</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a 
              href="/" 
              target="_blank"
              className="font-body text-sm text-sand/80 hover:text-white transition-colors"
            >
              Voir le site →
            </a>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-white hover:bg-white/10"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <h2 className="font-heading text-3xl font-bold text-forest">
            Bienvenue ! 👋
          </h2>
          <p className="font-body text-forest/70 mt-2">
            Gérez le contenu de votre site depuis ce tableau de bord.
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <Link
            to="/admin/blog/new"
            className="bg-forest hover:bg-forest-dark text-white rounded-2xl p-6 flex items-center gap-4 transition-colors"
          >
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Plus className="w-6 h-6" />
            </div>
            <div>
              <p className="font-body font-semibold">Nouvel article</p>
              <p className="font-body text-sm text-white/70">Ajouter une actualité</p>
            </div>
          </Link>

          <Link
            to="/admin/alumni/new"
            className="bg-autumn hover:bg-autumn-dark text-white rounded-2xl p-6 flex items-center gap-4 transition-colors"
          >
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Cat className="w-6 h-6" />
            </div>
            <div>
              <p className="font-body font-semibold">Nouveau chaton</p>
              <p className="font-body text-sm text-white/70">Ajouter un adopté</p>
            </div>
          </Link>

          <Link
            to="/admin/testimonials/new"
            className="bg-sand-dark hover:bg-sand text-forest rounded-2xl p-6 flex items-center gap-4 transition-colors"
          >
            <div className="w-12 h-12 bg-forest/10 rounded-xl flex items-center justify-center">
              <Star className="w-6 h-6" />
            </div>
            <div>
              <p className="font-body font-semibold">Nouveau témoignage</p>
              <p className="font-body text-sm text-forest/70">Ajouter un avis</p>
            </div>
          </Link>
        </div>

        {/* Menu Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {menuItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className="bg-white rounded-2xl p-6 shadow-soft hover:shadow-hover transition-shadow group"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 ${item.color} rounded-xl flex items-center justify-center`}>
                    <item.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-body font-semibold text-forest">{item.label}</p>
                    {item.count !== null && (
                      <p className="font-body text-sm text-forest/60">{item.count} éléments</p>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-forest/30 group-hover:text-autumn transition-colors" />
              </div>
            </Link>
          ))}
        </div>

        {/* Analytics */}
        <AnalyticsWidget />
      </main>
    </div>
  );
}
